import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Train, ArrowRight, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/search";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(next);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-center justify-center px-6 pt-safe-navbar">
      <div className="iq-card w-full max-w-md p-8 animate-fade-up">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <span className="inline-flex h-9 w-9 items-center justify-center bg-gold text-ink rounded-sm">
            <Train className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-display text-xl tracking-[0.18em] text-cream">INTERQUEST</span>
        </Link>
        <h1 className="font-display text-cream text-4xl tracking-wider mb-1">Welcome back</h1>
        <p className="font-serif italic text-cream-dim mb-6">The platform awaits.</p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="iq-label">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="iq-input"
            />
          </div>
          <div>
            <label className="iq-label">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="iq-input"
            />
            <div className="mt-2 text-right">
              <Link
                to="/forgot-password"
                data-testid="link-forgot-password"
                className="text-xs text-cream-dim hover:text-gold transition-colors"
              >
                Forgot password?
              </Link>
            </div>
          </div>
          {error && (
            <div data-testid="login-error" className="border border-accent-red/40 bg-accent-red/10 text-cream px-3 py-2 rounded-sm text-sm">
              {error}
            </div>
          )}
          <button data-testid="login-submit" type="submit" disabled={loading} className="iq-btn-primary w-full">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <p className="text-center text-sm text-cream-dim mt-6">
          New to InterQuest?{" "}
          <Link to="/register" data-testid="link-register" className="text-gold hover:text-cream">
            Join the journey
          </Link>
        </p>
      </div>
    </div>
  );
}
